import { createLogic } from "redux-logic";
import {
    type SetFilteredGradesType,
    type InitModelFilterType,
    type SetModelFilterType,
    INIT_MODEL_FILTER,
    SET_MODEL_FILTER_VALUE,
    SET_FILTERED_GRADES,
    setFilteredGrades,
    initModelFilter,
} from "../actions/ModelFilterActions";
import Debug from "../../../../../common-deprecated/Debug";
import type { BuildAndBuyLogicType } from "../store";
import type {
    ModelFilterCarType,
    ExtraType,
    ModelFilterGradeType,
} from "../../../../../common-deprecated/types/CommonTypes";
import { EMPTY_GUID } from "../../../../../common-deprecated/constants";
import { getCarImageFromConfig } from "../../../../shared-logic/utils/typedUtils";

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

/**
 * Helper function which filters cars based on the given filterValues object.
 * it matches the cars based on the provided filterValue(s), this supports providing all possible filter types, or just one.
 */
const filterCars = (cars: ModelFilterCarType[], filterValues: Record<string, string>): ModelFilterCarType[] => {
    const dedupe: string[] = [];
    return cars.reduce((carsArr, car) => {
        // Create id to check for duplicates.
        const dedupeId = `${car.grade.id}-${car.bodyTypeId}`;

        // When a currently selected filterValue isn't present in the filters of the car, the car will be removed.
        // Check if the filterValue code exists inside the car filterValues and if it does see that the value is right
        const carMatchesFilter = Object.keys(filterValues).every((filterValueKey) =>
            car.filterValues.find(
                (carFilterValue) =>
                    filterValueKey === carFilterValue.code && filterValues[filterValueKey] === carFilterValue.id,
            ),
        );

        // Some cars have the same grade/bodytype id but different selected options. These cars will be seen as duplicates
        // because selected options arent visible on the filterstep resulting in "duplicate" cars.
        // carMatchesFilter is required to make it so the car shown matches the filters
        if (dedupe.includes(dedupeId) && carMatchesFilter) {
            // Find the current grade/bodytype combo in the carsArray
            const currentBodyType = carsArr.find(
                (selectedCar) => selectedCar.bodyType.id === car.bodyType.id && selectedCar.grade.id === car.grade.id,
            );
            // Check if the price included in the carsArray is the lowest possible price. otherwise replace the grade with the one with lowest price
            if (currentBodyType && currentBodyType.price.cash > car.price.cash) {
                const foundIndex = carsArr.findIndex(
                    (selectedCar) =>
                        selectedCar.bodyType.id === car.bodyType.id && selectedCar.grade.id === car.grade.id,
                );
                carsArr.splice(foundIndex, 1, car);
                return carsArr;
            }

            return carsArr;
        }

        if (carMatchesFilter) {
            dedupe.push(dedupeId);
            return [...carsArr, car];
        }
        return carsArr;
    }, [] as ModelFilterCarType[]);
};

/**
 * Helper function to get the cars that are the closest match to the given filterValues.
 */
const getClosestMatch = (cars: ModelFilterCarType[], filterValues: Record<string, string>): ModelFilterCarType => {
    // Cars already matched the action.filterCode when this function is called.
    const scoredCars = cars
        .map((car) => ({
            ...car,
            score: Object.keys(filterValues).reduce((acc, filterValueKey) => {
                const filterValue = car.filterValues.find((carFilterValue) => filterValueKey === carFilterValue.code);
                if (filterValue) {
                    if (filterValues[filterValueKey] === filterValue.id) {
                        return acc + 1;
                    }
                }
                return acc;
            }, 0),
        }))
        .sort((a, b) => b.score - a.score);
    return scoredCars[0];
};

/**
 * Get a filterValue object from a modleFilter car object.
 */
const getFilterValuesFromCar = (car: ModelFilterCarType): Record<string, string> =>
    car.filterValues.reduce(
        (acc, filterValue) => {
            acc[filterValue.code] = filterValue.id;
            return acc;
        },
        {} as Record<string, string>,
    );

// ----------------------------------------------------------------------
// Logic
// ----------------------------------------------------------------------

/**
 * A new subset of cars is set. Calculate additional data to be shown.
 */
const newGradesLogic = (createLogic as BuildAndBuyLogicType<SetFilteredGradesType>)({
    type: SET_FILTERED_GRADES,

    transform({ getState, action }, next) {
        const { navigation, commonSettings } = getState();
        const { filteredGrades } = action;

        // Get dimensions from the first filtered car.
        if (filteredGrades.length > 0) {
            const { carId, selectedOptions, capacity, dimensions } = filteredGrades[0];

            const getImage = (width: number, height: number): string =>
                getCarImageFromConfig(
                    commonSettings,
                    "boot-exterior",
                    0,
                    navigation.model,
                    carId,
                    {
                        width,
                        height,
                        padding: 10,
                        scaleMode: 0,
                    },
                    undefined,
                    undefined,
                    undefined,
                    [],
                    selectedOptions as ExtraType[],
                );

            action.dimensions = {
                capacity,
                dimensions,
                bootImage: {
                    // TODO: Layout team please add correct srcSet images :)
                    default: getImage(500, 220),
                    set: `
                        ${getImage(520, 220)} 1x,
                        ${getImage(520, 220)} 2x, 
                        ${getImage(520, 220)} 3x,
                    `,
                },
            };
        }

        next(action);
    },
});

/**
 * User selected a new filter or the filters are initialized. Filter cars according to set filters.
 */
const newModelFilterLogic = (createLogic as BuildAndBuyLogicType<InitModelFilterType | SetModelFilterType>)({
    type: [SET_MODEL_FILTER_VALUE, INIT_MODEL_FILTER],

    validate({ getState, action }, allow, reject) {
        try {
            const { modelFilter, navigation } = getState();
            const { cars, filters } = modelFilter;

            let filterValues: Record<string, string>;
            const emptyDefaultCodes: string[] = [];
            if (action.type === INIT_MODEL_FILTER) {
                // FilterIndex found, load filter from car at the given index.
                if (navigation.filterIndex) {
                    filterValues = getFilterValuesFromCar(cars[navigation.filterIndex]);
                } else {
                    // Initialize will use the default filter values.
                    filterValues = filters.reduce(
                        (acc, filter) => {
                            // Keep track of empty defaults because these will need to be filled in with actual values.
                            if (filter.default === EMPTY_GUID) {
                                emptyDefaultCodes.push(filter.code);
                            }
                            acc[filter.code] = filter.default;
                            return acc;
                        },
                        {} as Record<string, string>,
                    );
                }
            } else {
                const { filterCode, value } = action;
                filterValues = { ...modelFilter.filterValues, [filterCode]: value };
            }

            // Initial filter. Filter all cars based on the given filterValues.
            let filteredGrades = filterCars(cars, filterValues);
            let rerunFilter = false;
            if (filteredGrades.length === 0) {
                // We have to rerun the filter if we don't get any results from the first filter.
                rerunFilter = true;
            } else if (action.type !== INIT_MODEL_FILTER) {
                // Make sure the user selected filter is present in the filtered grades.
                // This can happen when we have cars that don't have filtersValues for every filterCode.
                rerunFilter = !filteredGrades[0].filterValues.find((filter) => filter.code === action.filterCode);
            }

            // If the selected filter doesn't return any results we have to fall back and find filterValues which include
            // the user selected filter.
            if (rerunFilter) {
                if (action.type === SET_MODEL_FILTER_VALUE) {
                    // Filter grades based only on the selected filter.
                    const singleFilterGrades = filterCars(cars, { [action.filterCode]: action.value });
                    // If we still don't have any values, something went wrong. Log error and go back to defaults.
                    if (singleFilterGrades.length === 0) {
                        Debug.error("No grades found for the selected filter.");
                        return reject(initModelFilter());
                    }

                    // Filter grades again, but with the values from the first car containing the filter value.
                    filterValues = getFilterValuesFromCar(getClosestMatch(singleFilterGrades, filterValues));
                } else if (action.type === INIT_MODEL_FILTER) {
                    // Default filter doesn't return any grades, most likely because of a wrong configuration.
                    Debug.error("Default filter configuration is not valid.");

                    // Fallback: Use filters from the first car.
                    filterValues = getFilterValuesFromCar(cars[0]);
                }

                // Filter again, but with fallback filterValues. This should always return at least one result.
                filteredGrades = filterCars(cars, filterValues);
            }

            // Empty defaults found, use filters from the first filtered car.
            // This will guarantee at least one result so no fallback workflow needed.
            if (emptyDefaultCodes.length > 0) {
                emptyDefaultCodes.forEach((filterCode) => {
                    const filterValue = filteredGrades[0].filterValues.find((car) => car.code === filterCode);
                    if (filterValue) filterValues[filterCode] = filterValue.id;
                });

                filteredGrades = filterCars(cars, filterValues);
            }

            // Set filterValues from the first car. This car will also be used for dimension and slide rendering.
            action.filterValues = getFilterValuesFromCar(filteredGrades[0]);
            // Calculate the enabled values for each filter.

            action.filteredGrades = filteredGrades;
            allow(action);
        } catch (exception) {
            Debug.error("Failed validating modelfilter init/value update", exception);
        }
    },

    process({ action, getState }, dispatch, done) {
        const { navigation, commonSettings } = getState();

        const processedGrades: ModelFilterGradeType[] = (action.filteredGrades || [])
            // Create objects which match a GradeType object for easy usage in ModelViews.
            .map((car) => {
                const imageOptions = {
                    width: 261,
                    height: 150,
                    padding: 0,
                    scaleMode: 0,
                };
                const image = getCarImageFromConfig(
                    commonSettings,
                    "day-exterior",
                    9,
                    navigation.model,
                    car.id,
                    imageOptions,
                    undefined,
                    undefined,
                    undefined,
                    [],
                    car.selectedOptions as ExtraType[],
                );

                return {
                    id: car.grade.id,
                    carId: car.id,
                    name: car.grade.name,
                    bodyType: { id: car.bodyTypeId, index: 0 },
                    price: car.price,
                    finance: car.finance,
                    efficiencyClass: "", // Added this for now TODO: remove this or make it a seperate type
                    image,
                    capacity: car.capacity,
                    dimensions: car.dimensions,
                    selectedOptions: car.selectedOptions,
                    filterIndex: car.index,
                };
            })
            // Sort by name to have a consistent model list.
            .sort((a, b) => {
                const nameA = a.name;
                const nameB = b.name;

                return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
            });

        dispatch(setFilteredGrades(processedGrades, action.type === SET_MODEL_FILTER_VALUE ? action.filterCode : ""));

        done();
    },
});

export default [newModelFilterLogic, newGradesLogic];
